import React, { useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Centralized navigation motion settings. Change only these values to tune
// transitions across the whole application.
export const PAGE_TRANSITION_CONFIG = {
  duration: '180ms',
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  distance: '14px',
  enterOpacity: 0.92,
  background: '#f8fafc',
  reducedMotionDuration: '1ms'
};

const getLocationId = location =>
  `${location.key}:${location.pathname}:${location.search}:${location.hash}`;

/**
 * Route-level transition wrapper.
 *
 * It deliberately renders one route tree at a time, so leaving pages are
 * unmounted immediately and cannot retain duplicate realtime listeners.
 */
export default function PageTransition({ children }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const locationId = getLocationId(location);
  const historyRef = useRef({ entries: [locationId], index: 0 });
  const currentLocationIdRef = useRef(locationId);
  const hasNavigatedRef = useRef(false);
  const directionRef = useRef('forward');

  if (currentLocationIdRef.current !== locationId) {
    const history = historyRef.current;

    if (navigationType === 'POP') {
      const knownIndex = history.entries.indexOf(locationId);
      directionRef.current = knownIndex >= 0 && knownIndex > history.index ? 'forward' : 'back';
      if (knownIndex >= 0) {
        history.index = knownIndex;
      }
    } else if (navigationType === 'REPLACE') {
      history.entries[history.index] = locationId;
      directionRef.current = 'forward';
    } else {
      history.entries = history.entries.slice(0, history.index + 1);
      history.entries.push(locationId);
      history.index = history.entries.length - 1;
      directionRef.current = 'forward';
    }

    currentLocationIdRef.current = locationId;
    hasNavigatedRef.current = true;
  }

  const config = PAGE_TRANSITION_CONFIG;
  const isLegalPage = ['/privacy-policy', '/terms-conditions'].includes(location.pathname);
  const isScrollablePage = isLegalPage || ['/api', '/login'].includes(location.pathname);

  return (
    <>
      <style>{`
        .nexora-page-transition-viewport {
          position: fixed;
          inset: 0;
          overflow: hidden;
          background: ${config.background};
          isolation: isolate;
        }

        .nexora-page-transition-viewport.scrollable-page-viewport {
          overflow-x: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .nexora-page-transition-route {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          will-change: transform, opacity;
          transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        .nexora-page-transition-route.scrollable-page-route {
          position: relative;
          min-height: 100%;
          height: auto;
          overflow: visible;
        }

        .nexora-page-transition-route.forward {
          animation: nexoraPageEnterForward ${config.duration} ${config.easing} both;
        }

        .nexora-page-transition-route.back {
          animation: nexoraPageEnterBack ${config.duration} ${config.easing} both;
        }

        @keyframes nexoraPageEnterForward {
          from { transform: translate3d(${config.distance}, 0, 0); opacity: ${config.enterOpacity}; }
          to { transform: translate3d(0, 0, 0); opacity: 1; }
        }

        @keyframes nexoraPageEnterBack {
          from { transform: translate3d(-${config.distance}, 0, 0); opacity: ${config.enterOpacity}; }
          to { transform: translate3d(0, 0, 0); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .nexora-page-transition-route.forward,
          .nexora-page-transition-route.back {
            animation-duration: ${config.reducedMotionDuration};
          }
        }
      `}</style>

      <div className={`nexora-page-transition-viewport ${isScrollablePage ? 'scrollable-page-viewport' : ''}`}>
        <div
          key={locationId}
          className={`nexora-page-transition-route ${isScrollablePage ? 'scrollable-page-route' : ''} ${
            hasNavigatedRef.current ? directionRef.current : ''
          }`}
        >
          {children(location)}
        </div>
      </div>
    </>
  );
}
